import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
} from "@mui/material";
import { SelectedSettingsContext } from "../ts/Contexts";
import { IGameResult, IPenaltyItem, IRouletteGame } from "../ts/Definitions";

/**
 * Simple roulette dialog.
 * - accepts a game object with initialScore and requiredSpins (same shape used by FruitMachineDialog)
 * - user chooses a single bet per spin (odd/even, red/black, single number, split)
 * - winning a bet reduces the currentCount by a percentage:
 *     single number -> 50%
 *     split (two numbers) -> 25%
 *     odd/even or red/black -> 10%
 * - after requiredSpins spins the game ends; onClose called with penaltyCount set to remaining count
 */

const RED_NUMBERS = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36
]);

// Updated WheelPaper: background coloured red/black (green for 0)
const WheelPaper: React.FC<{ value: string | number }> = ({ value }) => {
  const n = Number(value);
  const isZero = n === 0;
  const isRed = RED_NUMBERS.has(n);
  const bgColor = isZero ? "#2e7d32" : isRed ? "#c62828" : "#212121"; // green, red, black
  const textColor = "#fff";

  return (
    <Paper
      elevation={2}
      sx={{
        width: 140,
        height: 140,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        fontSize: 36,
        mx: 1,
        bgcolor: bgColor,
        color: textColor,
        border: "2px solid rgba(255,255,255,0.08)",
      }}
    >
      <Typography variant="h3">{String(value)}</Typography>
    </Paper>
  );
};

type BetType = "none" | "oddEven" | "color" | "single" | "split";

interface CrapsDialogProps {
  game: IRouletteGame;
  onClose: (result: IGameResult) => void;
  penaltyKeys?: string[];
}

const CrapsDialog: React.FC<CrapsDialogProps> = ({ game, onClose, penaltyKeys }) => {
  const settings = useContext(SelectedSettingsContext);
  const { initialScore, requiredSpins, key: gameKey, userid, username, name } = game;
  const [penalties, setPenalties] = useState<IPenaltyItem[]>([]);
  useEffect(() => {
    const newPens = penaltyKeys?.map(k => settings.getPenalty?.(k)).filter(Boolean) as IPenaltyItem[] || [];
    setPenalties(newPens);
  }, [penaltyKeys]);

  const [currentCount, setCurrentCount] = useState<number>(initialScore ?? 0);
  const [spinsDone, setSpinsDone] = useState<number>(0);
  const [finished, setFinished] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [wheelValue, setWheelValue] = useState<number>(Math.floor(Math.random() * 37));
  const [message, setMessage] = useState<string | null>(null);

  // bet state
  const [betType, setBetType] = useState<BetType>("none");
  const [oddEven, setOddEven] = useState<"odd" | "even">("odd");
  const [colorBet, setColorBet] = useState<"red" | "black">("red");
  const [singleNumber, setSingleNumber] = useState<number | "">("");
  const [splitA, setSplitA] = useState<number | "">("");
  const [splitB, setSplitB] = useState<number | "">("");

  const timeoutsRef = useRef<number[]>([]);
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(t => clearTimeout(t));
    };
  }, []);

  const spinWheel = () => {
    if (spinning || finished) return;
    // require a bet selected
    if (betType === "none") {
      setMessage("Select a bet before spinning.");
      return;
    }
    setMessage(null);
    setSpinning(true);

    // animate wheel by quickly changing numbers
    const tempUpdates = 24;
    for (let i = 0; i < tempUpdates; i++) {
      const to = window.setTimeout(() => {
        setWheelValue(Math.floor(Math.random() * 37));
      }, i * 40);
      timeoutsRef.current.push(to);
    }

    // settle final value after animation
    const settle = window.setTimeout(() => {
      const final = Math.floor(Math.random() * 37);
      setWheelValue(final);

      // determine win
      let win = false;
      let reductionRate = 0;

      if (betType === "single") {
        if (final === Number(singleNumber)) {
          win = true;
          reductionRate = 0.5;
        }
      } else if (betType === "split") {
        if (String(splitA) !== "" && String(splitB) !== "") {
          const a = Number(splitA), b = Number(splitB);
          if (final === a || final === b) {
            win = true;
            reductionRate = 0.25;
          }
        }
      } else if (betType === "oddEven") {
        if (final !== 0) {
          const isEven = final % 2 === 0;
          if ((oddEven === "even" && isEven) || (oddEven === "odd" && !isEven)) {
            win = true;
            reductionRate = 0.10;
          }
        }
      } else if (betType === "color") {
        if (final !== 0) {
          const isRed = RED_NUMBERS.has(final);
          if ((colorBet === "red" && isRed) || (colorBet === "black" && !isRed)) {
            win = true;
            reductionRate = 0.10;
          }
        }
      }

      let newCount = currentCount;
      if (win) {
        const reduceAmount = Math.round(newCount * reductionRate * 100) / 100;
        newCount = Math.round((newCount - reduceAmount) * 100) / 100;
        setMessage(`Win! Applied ${Math.round(reductionRate * 100)}% reduction (${reduceAmount.toFixed(0)})`);
      } else {
        // LOSS: increase current count by 5%
        newCount = Math.round(newCount * 1.05 * 100) / 100;
        setMessage("No win this spin. +5% boost applied.");
      }

      setCurrentCount(newCount);
      setSpinsDone(s => s + 1);
      setSpinning(false);

      // clear timeouts used for animation
      timeoutsRef.current.forEach(t => clearTimeout(t));
      timeoutsRef.current = [];

      if (spinsDone + 1 >= (requiredSpins ?? 0)) {
        setFinished(true);
      }
    }, tempUpdates * 40 + 60);

    timeoutsRef.current.push(settle);
  };

  const handleClose = () => {
    const penaltyCount = Math.round(currentCount);
    onClose({
      gameKey,
      penaltyCount,
      completed: spinsDone,
      total: requiredSpins ?? 0,
      totalTime: spinsDone,
      mistakes: 0,
      userid: userid ?? settings.getControl().userid,
      username: username ?? settings.getControl().username,
      date: new Date(),
    } as IGameResult);
  };

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>{name ? `Roulette: ${name}` : "Roulette"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Spins required: {requiredSpins} — Spins done: {spinsDone}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Single number bets pay 50%, <br />Split bets pay 25%, <br />Odd/Even and Color bets pay 10%. <br />Losing bets increase your count by 5%.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Current count: <b>{currentCount.toFixed(0)}</b>
        </Typography>
        {penalties.length > 0 && (
          <Alert severity="warning" sx={{ my: 1 }}>
            Penalty: {penalties.map(p => p.name).join(", ")}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <WheelPaper value={wheelValue} />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Alert severity={finished ? "info" : "success"}>{message ?? (spinning ? "Spinning..." : "Place a bet and spin")}</Alert>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>Bet Type</InputLabel>
            <Select value={betType} label="Bet Type" onChange={(e) => setBetType(e.target.value as BetType)}>
              <MenuItem value="none">-- select --</MenuItem>
              <MenuItem value="oddEven">Odd / Even</MenuItem>
              <MenuItem value="color">Red / Black</MenuItem>
              <MenuItem value="single">Single Number</MenuItem>
              <MenuItem value="split">Split (two numbers)</MenuItem>
            </Select>
          </FormControl>

          {betType === "oddEven" && (
            <FormControl>
              <RadioGroup row value={oddEven} onChange={(_, v) => setOddEven(v as "odd" | "even")}>
                <FormControlLabel value="odd" control={<Radio />} label="Odd" />
                <FormControlLabel value="even" control={<Radio />} label="Even" />
              </RadioGroup>
            </FormControl>
          )}

          {betType === "color" && (
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel>Color</InputLabel>
              <Select value={colorBet} label="Color" onChange={(e) => setColorBet(e.target.value as "red" | "black")}>
                <MenuItem value="red">Red</MenuItem>
                <MenuItem value="black">Black</MenuItem>
              </Select>
            </FormControl>
          )}

          {betType === "single" && (
            <TextField
              label="Number (0-36)"
              type="number"
              inputProps={{ min: 0, max: 36 }}
              value={singleNumber}
              onChange={(e) => setSingleNumber(e.target.value === "" ? "" : Number(e.target.value))}
            />
          )}

          {betType === "split" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="A (0-36)"
                type="number"
                inputProps={{ min: 0, max: 36 }}
                value={splitA}
                onChange={(e) => setSplitA(e.target.value === "" ? "" : Number(e.target.value))}
              />
              <TextField
                label="B (0-36)"
                type="number"
                inputProps={{ min: 0, max: 36 }}
                value={splitB}
                onChange={(e) => setSplitB(e.target.value === "" ? "" : Number(e.target.value))}
              />
            </Box>
          )}
        </Box>

      </DialogContent>

      <DialogActions>
        {!finished && (
          <Button variant="contained" color="primary" onClick={spinWheel} disabled={spinning}>
            {spinning ? "Spinning..." : "Spin"}
          </Button>
        )}
        <Button variant="outlined" color={finished ? "primary" : "secondary"} onClick={handleClose} disabled={!finished}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrapsDialog;